import { toast } from "@carbon6/c6-frontend-design";
import {
  cloneTrainingSession,
  getTrainingSession,
  isWorkspaceNotOwnedError,
  renameTrainingSession,
  resumeTrainingSession,
} from "@/api";
import { isHttpClientError } from "@/http";
import { createLogger } from "@/logging";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset/loadState";
import { buildSessionResubmitPrefill } from "./submit/sessionResubmitPrefill";
import { createdSessionToListView } from "./trainingSessionFormat";
import { openTrainingSessionWindow } from "./trainingSessionWindow";
import { openTrainingSubmitWindow } from "./trainingSubmitWindow";

const logger = createLogger("page/training/sessionStartActions");

// 끝난(또는 실행 중인) 세션에서 **새 세션을 시작하는** 세 가지 동작. 셋 다 원본 세션을 그대로 두고
// 새 세션을 만든다. 그래서 한 자리에서 고르게 한다. 그러나 무엇을 물려받는지가 다르다:
//
// - clone   설정만 물려받는다(재현). 진행과 곡선은 물려받지 않는다.
// - resume  체크포인트와 부모 MLflow run 을 물려받는다. 곡선이 이어지고 설정은 바꿀 수 없다.
// - retrain 체크포인트만 물려받는다. 위저드가 열려 설정을 고칠 수 있고 곡선은 새로 시작한다.
//
// 세션 상세 헤더와 목록 행 ⋯ 메뉴가 같은 액션을 표시한다. 그래서 문구와 실행 절차의 소유자를 여기 둔다.
export type SessionStartActionKind = "clone" | "resume" | "retrain";

export type SessionStartActionText = {
  label: string;
  // 무엇을 물려받는지 한 줄. 헤더는 hover 툴팁으로, 행 메뉴는 항목 아래 caption 으로 쓴다.
  hint: string;
};

export const sessionStartActionText: Record<SessionStartActionKind, SessionStartActionText> = {
  clone: { label: "Session 복제", hint: "같은 설정으로 새 Session을 만듭니다." },
  resume: { label: "이어서 학습", hint: "멈춘 Checkpoint에서 시작합니다." },
  retrain: { label: "재학습", hint: "Checkpoint에서 설정을 바꿔 시작합니다." },
};

// resume 은 거절 코드의 뜻이 cancel/retry 와 달라 문구를 따로 구분한다. 특히 여기서 404 는 "재개할
// 체크포인트가 없다"는 뜻이다. 스코프 밖 404 와 코드는 같다. 그러나 버튼이 보였다면 이미 스코프
// 안이므로 체크포인트 쪽으로 안내하는 것이 맞다.
export function resolveResumeErrorMessage(error: unknown): string {
  if (isWorkspaceNotOwnedError(error)) {
    return "이 Session을 다른 사용자가 선점하고 있어 이어서 학습할 수 없습니다. 선점한 사용자에게 요청해 주세요.";
  }
  if (!isHttpClientError(error)) {
    return resolveLoadErrorMessage(error);
  }
  if (error.status === 404) {
    return "이어받을 Checkpoint가 없습니다. 에폭을 하나도 넘기지 못한 Training은 이어서 돌릴 수 없습니다.";
  }
  if (error.status === 409) {
    // 세 경우가 한 코드다(부모가 아직 끝나지 않음 · train step 2개 이상 · MLflow run 없음).
    // 화면은 어느 경우인지 알 수 없으므로 하나로 합치고, 상태 확인을 안내한다.
    return "이 Session은 이어서 학습할 수 없습니다. 아직 끝나지 않았거나 이어받을 수 없는 구성입니다. 최신 상태를 확인해 주세요.";
  }
  if (error.status === 503) {
    return "MLflow 를 사용할 수 없어 이어서 학습을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }
  return resolveLoadErrorMessage(error);
}

// clone 은 상태 제약이 없다(BE 게이트는 스코프 + 선점자/admin 뿐). 그래서 resume 같은 코드별 경우가
// 없다. 선점 이탈만 구분하고 나머지는 공통 문구로 전달한다.
export function resolveCloneErrorMessage(error: unknown): string {
  if (isWorkspaceNotOwnedError(error)) {
    return "이 Session을 다른 사용자가 선점하고 있어 복제할 수 없습니다. 선점한 사용자에게 요청해 주세요.";
  }
  return resolveLoadErrorMessage(error);
}

// 재학습이 열리지 않은 이유. 프로브 응답 전, "체크포인트 없음", "확인 실패"는 사용자가 할 행동이
// 다르다(기다린다 / 이 세션으로는 할 수 없다 / 다시 시도한다). 그래서 하나로 합치지 않는다.
export function resolveRetrainDisabledReason(state: LoadState<boolean>): string | null {
  switch (state.status) {
    case "idle":
    case "loading":
      return "Checkpoint를 확인하는 중입니다.";
    case "error":
      return "Checkpoint 여부를 확인하지 못했습니다. 잠시 후 다시 열어 주세요.";
    case "success":
      return state.data
        ? null
        : "재학습에 쓸 Checkpoint가 없습니다. 에폭을 하나도 넘기지 못한 Training은 다시 학습할 수 없습니다.";
  }
}

const startedSessionToast: Record<"clone" | "resume", string> = {
  clone: "같은 설정으로 새 Session을 만들었습니다.",
  resume: "Checkpoint에서 이어서 학습할 Session을 만들었습니다.",
};

const renameFailedToast: Record<"clone" | "resume", string> = {
  clone: "복제한 Session은 만들었지만 이름을 바꾸지 못했습니다. 상세에서 다시 시도해 주세요.",
  resume:
    "이어서 학습할 Session은 만들었지만 이름을 바꾸지 못했습니다. 상세에서 다시 시도해 주세요.",
};

export type StartSessionInput = {
  sessionId: string;
  displayName: string;
};

// clone·resume 실행. 둘 다 본문에 이름을 포함하지 않는 생성 경로다. 그래서 만든 뒤 rename 으로
// 사용자가 고른 이름을 적용하고 새 세션의 상세 창을 연다. 실패는 호출부가 문구로 바꾸도록 그대로 던진다.
//
// rename 실패는 전체 실패가 아니다. 이 시점에 새 세션은 이미 만들어져 GPU 를 차지한다. 실패로
// 처리해 창을 열지 않으면 사용자는 세션이 생긴 것을 모르고 다시 누른다.
export async function startSessionFrom(
  kind: "clone" | "resume",
  { sessionId, displayName }: StartSessionInput,
): Promise<void> {
  const created =
    kind === "clone"
      ? await cloneTrainingSession(sessionId)
      : await resumeTrainingSession(sessionId);

  let savedName: string | null = displayName;
  try {
    await renameTrainingSession(created.id, { display_name: displayName });
  } catch (error) {
    savedName = null;
    logger.error(`${kind} session rename failed`, error);
    toast.warning(renameFailedToast[kind]);
  }
  toast.info(startedSessionToast[kind]);
  openTrainingSessionWindow(createdSessionToListView(created, savedName), { owned: true });
}

// 재학습. 상세 스냅샷을 프리필로 옮겨 제출 위저드를 연다. 목록 행처럼 상세를 가지지 않은 호출부를
// 위해 여기서 상세를 받는다(상세 창은 이미 가진 rich 를 그대로 쓴다).
export async function openRetrainWizardForSession(sessionId: string, title: string): Promise<void> {
  const detail = await getTrainingSession(sessionId);
  const prefill = buildSessionResubmitPrefill(detail, title, {
    retrainSource: { id: sessionId, title },
  });
  openTrainingSubmitWindow({ prefill });
  if (prefill.omissions.length > 0) {
    toast.info(prefill.omissions.join(" "));
  }
}
